<template>
  <div
    class="flex overflow-y-auto flex-col flex-1"
    style="scrollbar-color: #666 transparent">
    <!-- Top Bar with Search and Sort -->
    <div
      class="flex justify-between items-center px-4 py-2 text-white"
      :class="collapsed ? 'hidden' : 'block'">
      <!-- Search Input -->
      <div class="flex items-center">
        <fa-icon icon="search" class="mr-2 text-xs" />
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search Lists"
          class="px-2 py-1 text-sm placeholder-gray-300 text-white bg-transparent rounded-md border-0 focus:outline-hidden focus:ring focus:ring-gray-300 sm:focus:ring-gray-500" />
      </div>

      <!-- Sort Order Button (cycles through options on click) -->
      <ToggleButton
        dark
        :active="sortOption !== 'newest'"
        :icon="sortIcon"
        @click="cycleSortOption">
        <span class="hidden sm:inline">{{ sortOptionLabel }}</span>
      </ToggleButton>
    </div>

    <!-- Lists -->
    <ul :class="collapsed ? 'px-0' : 'mb-24'" class="select-none">
      <li
        v-if="collapsed"
        class="px-3 py-2 text-center rounded-md transition-all">
        <button
          class="p-1 w-7 h-7 text-white rounded-full shadow-xs hover:bg-gray-600 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          @click.prevent="$emit('createList')">
          <fa-icon :icon="['fas', 'plus']" class="text-white" />
        </button>
      </li>

      <li class="mb-4">
        <ul>
          <template v-for="list in combinedLists" :key="list.id">
            <li
              :class="
                list.is_system && list.system_type === 'watchlist'
                  ? 'border-b border-white/10 pb-3 mb-3'
                  : ''
              ">
              <template v-if="links">
                <router-link
                  :to="getListRoute(list)"
                  class="flex px-3 py-2 w-full h-auto rounded-md hover:bg-gray-800"
                  active-class="bg-gray-600/50 sm:bg-gray-700">
                  <ListCard :list="list" :collapsed="collapsed" />

                  <slot name="action" :list="list"></slot>
                </router-link>
              </template>
              <template v-else>
                <div
                  class="flex px-3 py-2 w-full h-auto rounded-md cursor-pointer hover:bg-gray-800"
                  @click.prevent="$emit('list-selected', list.id)">
                  <ListCard :list="list" :collapsed="collapsed" />
                  <slot name="action" :list="list"></slot>
                </div>
              </template>
            </li>
          </template>

          <template v-if="combinedLists.length === 0">
            <li
              class="flex px-3 py-2 w-full h-auto rounded-md border-t border-gray-700 opacity-50 hover:bg-gray-800">
              <div class="flex text-sm items">No lists found</div>
            </li>
          </template>
        </ul>
      </li>
    </ul>
  </div>
</template>
<script setup>
import { ref, computed } from 'vue'
import ListCard from '@/components/Pure/ListCard'
import ToggleButton from '@/components/Pure/ToggleButton.vue'
import { useFeatureFlags } from '@/use/useFeatureFlags'

const { hasFeature } = useFeatureFlags()
const searchQuery = ref('')

const props = defineProps({
  collapsed: Boolean,
  lists: Array,
  listsFollowing: Array,
  links: {
    type: Boolean,
    default: true
  }
})

defineEmits(['list-selected', 'createList'])

// Sort option state - cycles through options
const sortOption = ref('newest') // 'newest' | 'alphabetical' | 'updated' | 'count'

const sortOptionLabel = computed(() => {
  switch (sortOption.value) {
    case 'alphabetical':
      return 'A-Z'
    case 'updated':
      return 'Updated'
    case 'count':
      return 'Items'
    default:
      return 'Newest'
  }
})

const sortIcon = computed(() => {
  switch (sortOption.value) {
    case 'alphabetical':
      return 'arrow-down-a-z'
    case 'updated':
      return ['far', 'clock']
    case 'count':
      return 'list'
    default:
      return 'arrow-down-wide-short'
  }
})

const cycleSortOption = () => {
  const options = ['newest', 'alphabetical', 'updated', 'count']
  const currentIdx = options.indexOf(sortOption.value)
  const nextIdx = (currentIdx + 1) % options.length
  sortOption.value = options[nextIdx]
}

const filterAndSortLists = (lists) => {
  let filteredLists = lists.value.filter((list) =>
    list.title.toLowerCase().includes(searchQuery.value.toLowerCase())
  )

  // Separate watchlist from other lists
  const watchlist = filteredLists.find(
    (list) => list.is_system && list.system_type === 'watchlist'
  )
  const otherLists = filteredLists.filter(
    (list) => !(list.is_system && list.system_type === 'watchlist')
  )

  // Sort only the non-watchlist lists
  switch (sortOption.value) {
    case 'newest':
      otherLists.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      break
    case 'alphabetical':
      otherLists.sort((a, b) => a.title.localeCompare(b.title))
      break
    case 'updated':
      otherLists.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
      break
    case 'count':
      otherLists.sort((a, b) => {
        const countA = a.list_items?.[0]?.count ?? 0
        const countB = b.list_items?.[0]?.count ?? 0
        return countB - countA
      })
      break
  }

  // Return watchlist first, then sorted other lists
  return watchlist ? [watchlist, ...otherLists] : otherLists
}

// Combine auto and non-auto lists into a single array
const listsFollowing = computed(() => {
  return props.listsFollowing ? props.listsFollowing : []
})
const allLists = computed(() => [...props.lists, ...listsFollowing.value])
const combinedLists = computed(() => filterAndSortLists(allLists))

// Route helper for system lists - link to profile views when watch_status_v2 is enabled
const getListRoute = (list) => {
  const slug = list.slug || list.id

  // Only use profile views when watch_status_v2 feature is enabled
  if (hasFeature('watch_status_v2')) {
    if (slug === 'watchlist') {
      return { name: 'WebProfile', query: { view: 'log', status: 'watchlist' } }
    }
    if (slug === 'seen') {
      return { name: 'WebProfile', query: { view: 'log', status: 'seen' } }
    }
    if (slug === 'favourites') {
      return { name: 'WebProfile', query: { view: 'favourites' } }
    }
  }

  // Default: regular list page
  return { name: 'WebListSingle', params: { listId: slug } }
}
</script>
